// What a share's row offers in its ⋯ menu, on /shares and on a group's page
// (byj-1wv.51.5): Edit, for its owner alone (byj-1wv.38), and Delete or
// Remove, each asked first (byj-1wv.34). The page asks in its own dialogs
// and keeps its own list, so each step says back what it changed.
import { api, type ShareSummary } from "../api";
import { t } from "../i18n";
import type { ConfirmOptions } from "./Confirm";
import type { MenuItem } from "./RowMenu";

/** Its owner deletes an ended share (not a class's: it stays with the class); anyone else takes it off their list. */
export const deletable = (s: ShareSummary) => (s.role === "owner" ? !s.live && !s.class_id : true);

export type ShareActions = {
  rename: (s: ShareSummary) => void;
  drop: (s: ShareSummary) => void;
  /** Several at once, after one question: what was deleted. */
  dropMany: (ids: string[]) => Promise<string[]>;
};

/** A share's row menu: Edit for its owner, then Delete for its owner or Remove for anyone else, where it may go. */
export function shareMenu(s: ShareSummary, actions: Pick<ShareActions, "rename" | "drop">): MenuItem[] {
  const owner = s.role === "owner";
  return [
    ...(owner ? [{ label: t("common:edit"), onSelect: () => actions.rename(s) }] : []),
    ...(deletable(s) ? [{ label: owner ? t("recordings:deleteShare") : t("recordings:removeShare"), onSelect: () => actions.drop(s), danger: true }] : []),
  ];
}

/** The steps behind the menu, asked in the page's `confirm` and `editTitle`; `renamed` and `gone` tell the page what changed. */
export function shareActions({ confirm, editTitle, renamed, gone, fail }: {
  confirm: (message: string, options: ConfirmOptions) => Promise<boolean>;
  editTitle: (heading: string, value: string) => Promise<string | null>;
  renamed: (id: string, title: string) => void;
  gone: (ids: string[]) => void;
  fail: (err: unknown) => void;
}): ShareActions {
  const rename = async (s: ShareSummary) => {
    const title = await editTitle(t("recordings:editShareHeading"), s.title || "");
    if (title === null) return;
    try {
      renamed(s.id, (await api.updateShare(s.id, { title })).title);
    } catch (err) {
      fail(err);
    }
  };

  const drop = async (s: ShareSummary) => {
    const owner = s.role === "owner";
    const title = s.title || s.id;
    if (!(await confirm(owner ? t("recordings:deleteShareConfirm", { title }) : t("recordings:removeShareConfirm", { title }), { action: owner ? t("recordings:deleteShare") : t("recordings:removeShare"), danger: true })))
      return;
    try {
      await api.deleteShare(s.id);
      gone([s.id]);
    } catch (err) {
      fail(err);
    }
  };

  // several at once, after one question (byj-1wv.43): the owner's are
  // deleted, the rest left; what was deleted is said back
  const dropMany = async (ids: string[]): Promise<string[]> => {
    if (ids.length === 0 || !(await confirm(t("recordings:deleteSharesConfirm", { count: ids.length }), { action: t("recordings:deleteShare"), danger: true }))) return [];
    const done: string[] = [];
    for (const id of ids) {
      try {
        await api.deleteShare(id);
        done.push(id);
      } catch (err) {
        fail(err);
      }
    }
    gone(done);
    return done;
  };

  return { rename: (s) => void rename(s), drop: (s) => void drop(s), dropMany };
}
